<template>
  <div class="pdf-uploader">
    <div class="upload-container">
      <h2>Upload PDF</h2>
      <div
        class="drop-zone"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
        :class="{ dragging: isDragging }"
      >
        <input type="file" ref="fileInput" accept=".pdf" @change="handleFileSelect" style="display: none" />

        <div v-if="!uploadedFile">
          <p>Drag and drop the PDF file here or</p>
          <button @click="$refs.fileInput.click()" class="btn-primary">Choose a file</button>
        </div>

        <div v-else class="file-info">
          <p>Файл: {{ uploadedFile.name }}</p>
          <p>Размер: {{ formatFileSize(uploadedFile.size) }}</p>
          <button @click="uploadFile" :disabled="uploading" class="btn-success">
            {{ uploading ? 'Loading...' : 'Upload' }}
          </button>
          <button @click="clearFile" class="btn-secondary">Cancel</button>
        </div>
      </div>

      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <div v-if="extractedData" class="results">
        <h3>Parse data</h3>

        <div class="data-card">
          <div class="data-row">
            <strong>NI Number:</strong>
            <span class="highlight">{{ extractedData.niNumber || 'Not found' }}</span>
          </div>

          <div class="data-row">
            <strong>Our Reference:</strong>
            <span>{{ extractedData.ourReference || 'Not found' }}</span>
          </div>

          <div class="data-row">
            <strong>Your Reference:</strong>
            <span>{{ extractedData.yourReference || 'Not found' }}</span>
          </div>

          <div class="data-row">
            <strong>Date:</strong>
            <span>{{ extractedData.letterDate || 'Not found' }}</span>
          </div>

          <div class="data-row">
            <strong>Qualifying Years:</strong>
            <span>{{ extractedData.qualifyingYears || 'Not found' }}</span>
          </div>
        </div>

        <button @click="copyToClipboard" class="btn-copy">Copy all data</button>
      </div>
    </div>
  </div>
</template>

<script>
import pdfService from '@/services/pdf-service/pdf.service';
export default {
  name: 'PDFUploader',
  data() {
    return {
      uploadedFile: null,
      uploading: false,
      extractedData: null,
      error: null,
      isDragging: false
    };
  },
  methods: {
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file && file.type === 'application/pdf') {
        this.uploadedFile = file;
        this.error = null;
      } else {
        this.error = 'Please select the PDF file';
      }
    },

    handleDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer.files[0];
      if (file && file.type === 'application/pdf') {
        this.uploadedFile = file;
        this.error = null;
      } else {
        this.error = 'Please select the PDF file';
      }
    },

    async uploadFile() {
      if (!this.uploadedFile) return;

      this.uploading = true;
      this.error = null;

      const formData = new FormData();
      formData.append('pdf', this.uploadedFile);

      try {
        const { base64, fileType, fileName } = await this.convertFileToBase64(this.uploadedFile);
        console.log(base64, fileType, fileName);
        const file = {
          base64: base64,
          fileType: fileType,
          fileName: fileName
        };
        const { extractedData } = await pdfService.uploadPDF(file);
        console.log('Response:', extractedData);
        if (extractedData) {
          this.extractedData = extractedData;
        } else {
          this.error = 'Error when extracting data';
        }
      } catch (err) {
        this.error = err.response?.data?.error || 'Error when uploading a file';
        console.error(err);
      } finally {
        this.uploading = false;
      }
    },
    convertFileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = function (e) {
          // Extract base64 data (remove data:image/png;base64, prefix)
          const base64Data = e.target.result.split(',')[1];
          const fileType = file.type.split('/')[1];
          resolve({
            base64: base64Data,
            fileType: fileType,
            fileName: file.name,
            fileSize: file.size
          });
        };

        reader.onerror = function (error) {
          reject(error);
        };

        reader.readAsDataURL(file);
      });
    },
    clearFile() {
      this.uploadedFile = null;
      this.extractedData = null;
      this.error = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    async copyToClipboard() {
      const dataStr = JSON.stringify(this.extractedData, null, 2);
      try {
        await navigator.clipboard.writeText(dataStr);
        alert('The data is copied to the clipboard');
      } catch (err) {
        console.error('Copy error:', err);
      }
    }
  }
};
</script>

<style scoped>
.pdf-uploader {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.upload-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 30px;
}

.drop-zone {
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  transition: all 0.3s ease;
}

.drop-zone.dragging {
  border-color: #4caf50;
  background: #f0f8ff;
}

.btn-primary {
  background: #2196f3;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  margin-top: 10px;
  font-size: 14px;
}

.btn-primary:hover {
  background: #1976d2;
}

.btn-success {
  background: #4caf50;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  margin: 5px;
}

.btn-success:hover:not(:disabled) {
  background: #45a049;
}

.btn-secondary {
  background: #f44336;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  margin: 5px;
}

.btn-copy {
  background: #ff9800;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  margin-top: 20px;
  width: 100%;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-message {
  background: #ffebee;
  color: #c62828;
  padding: 10px;
  border-radius: 5px;
  margin-top: 15px;
}

.results {
  margin-top: 30px;
}

.data-card {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 20px;
  margin-top: 15px;
}

.data-row {
  padding: 10px;
  border-bottom: 1px solid #ddd;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.data-row strong {
  min-width: 150px;
}

.highlight {
  background: #fff3cd;
  padding: 3px 8px;
  border-radius: 4px;
  font-family: monospace;
  font-weight: bold;
}

.shortfall-years {
  margin-top: 15px;
}

.shortfall-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

.shortfall-table th,
.shortfall-table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}

.shortfall-table th {
  background: #e0e0e0;
}

.file-info {
  text-align: center;
}

.file-info p {
  margin: 5px 0;
}

h2,
h3 {
  color: #333;
  margin-bottom: 20px;
}
</style>
